import { Link } from 'react-router-dom';

export default function SeoIntroSection() {
  return (
    <section className="relative py-20 md:py-28 px-4 md:px-10 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4" style={{ fontFamily: 'var(--font-label)' }}>
            Learn More
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            What Is Superbuy Sheet?
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto" style={{ fontFamily: 'var(--font-body)' }}>
            Everything you need to know about navigating the world of Superbuy Spreadsheet shopping
          </p>
        </div>

        <article className="prose-custom space-y-6">
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-background-200">
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <p className="text-foreground-700 leading-relaxed text-base" style={{ fontFamily: 'var(--font-body)' }}>
                  <strong className="text-foreground-950 font-semibold">Superbuy Sheet</strong> is a carefully organized
                  resource system that helps international shoppers discover, compare, and acquire products through structured
                  spreadsheet collections. Instead of spending hours searching through countless listings, Superbuy Sheet
                  consolidates the best product finds across multiple categories — from footwear and apparel to accessories
                  and lifestyle goods — into one accessible, regularly updated platform.
                </p>
              </div>
              <img
                src="https://readdy.ai/api/search-image?query=Carefully%20arranged%20fashion%20accessories%20and%20lifestyle%20objects%20on%20a%20clean%20white%20desk%2C%20including%20a%20pair%20of%20premium%20white%20leather%20sneakers%20placed%20next%20to%20a%20soft%20beige%20cashmere%20scarf%2C%20a%20minimalist%20notebook%20with%20a%20brass%20pen%2C%20a%20ceramic%20coffee%20mug%20with%20delicate%20latte%20art%2C%20a%20small%20potted%20succulent%20plant%2C%20soft%20natural%20daylight%20streaming%20through%20sheer%20curtains%2C%20warm%20and%20inviting%20editorial%20still%20life%2C%20gentle%20pastel%20neutral%20tones%2C%20relaxed%20lifestyle%20blogger%20aesthetic%2C%20clean%20composition%20with%20organic%20textures&width=600&height=400&seq=seo-intro-lifestyle-2026&orientation=landscape"
                alt="Beautifully curated Superbuy Spreadsheet lifestyle product photography"
                title="Superbuy Spreadsheet - Lifestyle Product Inspiration"
                className="w-full lg:w-56 rounded-2xl object-cover flex-shrink-0"
                style={{ height: '180px' }}
              />
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 border border-background-200">
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
              Why Shoppers Love Superbuy Spreadsheet
            </h3>
            <p className="text-foreground-700 leading-relaxed text-base mb-5" style={{ fontFamily: 'var(--font-body)' }}>
              The traditional way of finding products through agent platforms can be time-consuming and overwhelming.
              Superbuy Spreadsheet changes the game by offering a curated, category-based browsing experience. Whether
              you are hunting for the latest sneaker releases, cozy hoodies, stylish jackets, or unique accessories,
              the spreadsheet format ensures you never miss a hidden gem.
            </p>
            <p className="text-foreground-700 leading-relaxed text-base" style={{ fontFamily: 'var(--font-body)' }}>
              Each category is meticulously updated with fresh finds, making Superbuy Sheet the go-to destination for
              shoppers who value efficiency and quality. The platform serves as an independent research hub — not affiliated
              with any single seller — ensuring unbiased product recommendations across all 14 categories.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 border border-background-200 overflow-x-auto">
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5">
              Superbuy Spreadsheet vs Traditional Search
            </h3>
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-3 pr-4 text-sm font-semibold text-foreground-800" style={{ fontFamily: 'var(--font-label)' }}>Feature</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-400" style={{ fontFamily: 'var(--font-label)' }}>Traditional Search</th>
                  <th className="text-left py-3 pl-4 text-sm font-semibold text-primary-600" style={{ fontFamily: 'var(--font-label)' }}>Superbuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-100">
                  <td className="py-3.5 pr-4 text-sm text-foreground-700 font-medium" style={{ fontFamily: 'var(--font-body)' }}>Finding Products</td>
                  <td className="py-3.5 px-4 text-sm text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Time consuming</td>
                  <td className="py-3.5 pl-4 text-sm text-primary-700 font-medium" style={{ fontFamily: 'var(--font-body)' }}>
                    <span className="flex items-center gap-1.5"><i className="ri-check-line text-accent-500"></i> Organized &amp; fast</span>
                  </td>
                </tr>
                <tr className="border-b border-background-100">
                  <td className="py-3.5 pr-4 text-sm text-foreground-700 font-medium" style={{ fontFamily: 'var(--font-body)' }}>Product Categories</td>
                  <td className="py-3.5 px-4 text-sm text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Unstructured</td>
                  <td className="py-3.5 pl-4 text-sm text-primary-700 font-medium" style={{ fontFamily: 'var(--font-body)' }}>
                    <span className="flex items-center gap-1.5"><i className="ri-check-line text-accent-500"></i> 14 structured categories</span>
                  </td>
                </tr>
                <tr className="border-b border-background-100">
                  <td className="py-3.5 pr-4 text-sm text-foreground-700 font-medium" style={{ fontFamily: 'var(--font-body)' }}>Research Speed</td>
                  <td className="py-3.5 px-4 text-sm text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Slow &amp; manual</td>
                  <td className="py-3.5 pl-4 text-sm text-primary-700 font-medium" style={{ fontFamily: 'var(--font-body)' }}>
                    <span className="flex items-center gap-1.5"><i className="ri-check-line text-accent-500"></i> Faster browsing</span>
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 pr-4 text-sm text-foreground-700 font-medium" style={{ fontFamily: 'var(--font-body)' }}>Product Comparison</td>
                  <td className="py-3.5 px-4 text-sm text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Difficult</td>
                  <td className="py-3.5 pl-4 text-sm text-primary-700 font-medium" style={{ fontFamily: 'var(--font-body)' }}>
                    <span className="flex items-center gap-1.5"><i className="ri-check-line text-accent-500"></i> Simple &amp; intuitive</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 border border-background-200">
            <div className="flex flex-col lg:flex-row-reverse gap-8 items-start">
              <div className="flex-1">
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  Getting Started With Superbuy Sheet
                </h3>
                <p className="text-foreground-700 leading-relaxed text-base" style={{ fontFamily: 'var(--font-body)' }}>
                  Getting started is straightforward. Simply browse the categories above, select the products you are
                  interested in, and you will be directed to our main website where the full Superbuy Spreadsheet
                  collections await. Each category link leads to a dedicated page filled with hand-picked items,
                  complete with pricing details, style options, and shopping references. The platform is designed for
                  both newcomers and experienced international shoppers — no prior knowledge of agent-based shopping
                  is required to start discovering great products through Superbuy Sheet today.
                </p>
              </div>
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20desk%20setup%20with%20an%20open%20laptop%20showing%20a%20minimalist%20product%20catalog%20interface%2C%20surrounded%20by%20aesthetic%20stationery%20including%20a%20small%20notebook%20with%20a%20leather%20cover%2C%20a%20sleek%20white%20ceramic%20pen%20holder%2C%20a%20dainty%20gold%20desk%20lamp%20with%20warm%20light%2C%20a%20pair%20of%20stylish%20round%20sunglasses%2C%20and%20a%20steaming%20cup%20of%20matcha%20latte%2C%20soft%20natural%20window%20light%20creating%20a%20calm%20cozy%20atmosphere%2C%20warm%20neutral%20tones%20with%20sage%20green%20accents%2C%20flat%20lay%20meets%20desk%20styling%2C%20sophisticated%20yet%20approachable%2C%20boutique%20coworking%20space%20vibe&width=600&height=400&seq=seo-started-workspace-2026&orientation=landscape"
                alt="Superbuy Spreadsheet - Getting started with organized shopping workspace"
                title="Superbuy Sheet - Start Your Shopping Journey"
                className="w-full lg:w-56 rounded-2xl object-cover flex-shrink-0"
                style={{ height: '180px' }}
              />
            </div>
          </div>
        </article>

        <div className="mt-10 mb-8">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5 text-center">
            Explore Our In-Depth Guides
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Link
              to="/superbuy-sheet-guide"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-book-open-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Complete Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>What is Superbuy Sheet?</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-review"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-star-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Honest Review</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Is it worth using?</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-beginner-guide"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-user-smile-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Beginner Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>First time? Start here</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-shoes"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-footprint-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Shoes Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Footwear buying tips</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-hoodies"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-t-shirt-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Hoodies Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Knitwear &amp; sweaters</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-best-finds"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-fire-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Best Finds</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Top picks of 2026</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-jackets"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-shirt-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Jackets Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Outerwear discoveries</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-tshirts"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-checkbox-blank-circle-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>T-Shirts Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Quality tee curation</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-pants"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-pantone-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Pants Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Jeans &amp; trousers</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-bags"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-briefcase-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Bags Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Leather &amp; everyday carry</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-accessories"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-vip-crown-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Accessories</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Watches &amp; jewelry</span>
              </div>
            </Link>
            <Link
              to="/superbuy-spreadsheet-jersey"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer bg-white"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors flex-shrink-0">
                <i className="ri-basketball-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-foreground-800 block truncate" style={{ fontFamily: 'var(--font-label)' }}>Jersey Guide</span>
                <span className="text-xs text-foreground-500" style={{ fontFamily: 'var(--font-body)' }}>Sportswear &amp; teams</span>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}